import { isCustomParam, isServerSideMethod } from './components/TrackingLinkOptionsTab/utils';
import {
    ATTRIBUTION_TAB_IDS,
    CLICK_IMPRESSION_REPORTING_METHODS,
    MACRO_INPUT_TYPES,
    PLATFORMS,
    generalPlatformsIncludeTrackingLinkExtraRulePlatform,
    POSTBACK_EVENT_TYPE_OPTIONS,
    REQUIRED_SERVER_SIDE_MACRO_KEYS,
    TRACKING_LINK_EXTRA_REQUIRED_KEYS,
    TRACKING_LINK_EXTRA_RULE_PLATFORMS,
    STANDARD_TRACKING_LINK_PARAMETERS,
    TRANSLATE_PREFIX,
    URL_QUERY_KEY_REGEX,
    WHITESPACE_REGEX,
} from './consts';

export const PRIMARY_TRACKING_LINK_KEYS = new Set(
    STANDARD_TRACKING_LINK_PARAMETERS.filter(p => p.primary).map(p => p.singularKey)
);

// Formik error key suffixes that block saving as a draft (structural / format issues that the BE
// schema rejects even in draft mode). Other Formik errors are submit-only required-field misses.
const DRAFT_BLOCKING_ERROR_KEY_SUFFIXES = [
    '.platform_template',
    '.tracking_link_parameters_macro_no_spaces',
    '.tracking_link_parameters_duplicate_key',
    '.event_names_duplicate',
    '.event_parameter_key_no_spaces',
    '.event_names_no_spaces',
    '.partner_parameter_key_invalid_chars',
    '.partner_parameter_key_duplicate',
];

export const isDraftBlockingErrorKey = key => DRAFT_BLOCKING_ERROR_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));

const err = key => `${TRANSLATE_PREFIX}.ERRORS.${key}`;

// Default empty check: null/undefined, empty string, or empty array.
// Does NOT treat `false` as empty (handles boolean fields like view_through_campaigns).
const defaultIsEmpty = value =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

// Generic runner: for each rule, skip if `when` is false, then check emptiness.
// rule shape: { field, errorKey, when?, isEmpty? }
const validateRequired = (tabId, data, rules) =>
    rules.reduce((errors, { field, errorKey, when, isEmpty = defaultIsEmpty }) => {
        if (when && !when(data)) return errors;
        if (isEmpty(data[field], data)) {
            errors[`formData.${tabId}.${field}`] = err(errorKey);
        }
        return errors;
    }, {});

const GENERAL_RULES = [
    { field: 'platform_template', errorKey: 'PLATFORM_TEMPLATE_REQUIRED' },
    { field: 'platforms', errorKey: 'PLATFORMS_REQUIRED', when: d => !!d.platform_template },
    // Special: platforms_other only required when 'other' is selected
    {
        field: 'platforms_other',
        errorKey: 'PLATFORMS_OTHER_REQUIRED',
        when: d => !!d.platform_template && d.platforms?.includes(PLATFORMS.OTHER),
        isEmpty: v => !v?.trim(),
    },
    { field: 'campaign_objectives', errorKey: 'OBJECTIVES_REQUIRED', when: d => !!d.platform_template },
    // Data scope fields only required when receive_postbacks is explicitly true.
    // Mirrors backend `field_equals("receive_postbacks", True)` — absent ≠ true.
    {
        field: 'installs_data_scope',
        errorKey: 'INSTALLS_DATA_SCOPE_REQUIRED',
        when: d => !!d.platform_template && d.receive_postbacks === true,
    },
    {
        field: 'events_data_scope',
        errorKey: 'EVENTS_DATA_SCOPE_REQUIRED',
        when: d => !!d.platform_template && d.receive_postbacks === true,
    },
];

const TRACKING_LINK_OPTIONS_RULES = [
    { field: 'view_through_campaigns', errorKey: 'VIEW_THROUGH_REQUIRED' },
    { field: 'click_impression_reporting_method', errorKey: 'REPORTING_METHOD_REQUIRED' },
    // Special: elaboration only required when method is 'both'
    {
        field: 'click_impression_reporting_method_elaboration',
        errorKey: 'ELABORATION_REQUIRED',
        when: d => d.click_impression_reporting_method === CLICK_IMPRESSION_REPORTING_METHODS.BOTH,
        isEmpty: v => !v?.trim(),
    },
    // tracking_link_parameters must be non-empty
    {
        field: 'tracking_link_parameters',
        errorKey: 'TRACKING_LINK_PARAMS_REQUIRED',
    },
];

const validateTrackingLinkItems = (params, errors) => {
    const tabId = ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS;
    const hasIncompleteCustomRow = params.some(
        p => isCustomParam(p) && (!p.singular_key || !p.field_name || !p.partner_macro)
    );
    if (hasIncompleteCustomRow) {
        errors[`formData.${tabId}.tracking_link_parameters_item_fields`] = err('TRACKING_LINK_PARAM_FIELDS_REQUIRED');
    }

    params.forEach((p, index) => {
        if (!p.partner_macro?.trim()) {
            errors[`formData.${tabId}.tracking_link_parameters.${index}.partner_macro_required`] = {
                messageKey: err('TRACKING_LINK_PARAM_MACRO_REQUIRED_ROW'),
                fieldName: p.field_name?.trim() || p.singular_key || '',
            };
        }
    });

    const hasMacroWithSpaces = params.some(p => p.partner_macro && WHITESPACE_REGEX.test(p.partner_macro));
    if (hasMacroWithSpaces) {
        errors[`formData.${tabId}.tracking_link_parameters_macro_no_spaces`] = err(
            'TRACKING_LINK_PARAM_MACRO_NO_SPACES_SUMMARY'
        );
    }

    const seen = new Set();
    const hasDuplicateKey = params.some(p => {
        if (!p.singular_key) return false;
        if (seen.has(p.singular_key)) return true;
        seen.add(p.singular_key);
        return false;
    });
    if (hasDuplicateKey) {
        errors[`formData.${tabId}.tracking_link_parameters_duplicate_key`] = err('TRACKING_LINK_PARAM_DUPLICATE_KEY');
    }
};

const validateTrackingLinkOptions = (data, formData = {}) => {
    const errors = validateRequired(ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS, data, TRACKING_LINK_OPTIONS_RULES);
    const params = data.tracking_link_parameters || [];

    if (params.length) {
        validateTrackingLinkItems(params, errors);
    }

    const reportingMethod = data.click_impression_reporting_method;
    const generalPlatforms = formData[ATTRIBUTION_TAB_IDS.GENERAL]?.platforms;

    // Server-side and extra-rule checks are independent — BE emits both when applicable.
    if (isServerSideMethod(reportingMethod)) {
        const missingKeys = REQUIRED_SERVER_SIDE_MACRO_KEYS.filter(key => {
            const param = params.find(p => p.singular_key === key);
            return !param?.partner_macro?.trim();
        });
        if (missingKeys.length) {
            errors[`formData.${ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS}.tracking_link_parameters_server_side`] = {
                messageKey: err('TRACKING_LINK_PARAMS_SERVER_SIDE_REQUIRED'),
                requiredKeys: [...REQUIRED_SERVER_SIDE_MACRO_KEYS],
            };
        }
    }
    if (generalPlatformsIncludeTrackingLinkExtraRulePlatform(generalPlatforms)) {
        const missingExtraKeys = TRACKING_LINK_EXTRA_REQUIRED_KEYS.filter(key => {
            const param = params.find(p => p.singular_key === key);
            return !param?.partner_macro?.trim();
        });
        if (missingExtraKeys.length) {
            const triggeringPlatforms = (generalPlatforms || []).filter(p =>
                TRACKING_LINK_EXTRA_RULE_PLATFORMS.includes(p)
            );
            errors[`formData.${ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS}.tracking_link_extra`] = {
                messageKey: err('TRACKING_LINK_PARAMS_EXTRA_RULE'),
                triggeringPlatforms,
                missingKeys: missingExtraKeys,
            };
        }
    }

    return errors;
};

const validatePostbackOverallSetup = data => {
    const errors = {};
    const tabPath = `formData.${ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP}`;
    const entries = data.postback_events_and_platforms || [];
    const hasAnySelected = entries.some(e => (e.platforms || []).length > 0);
    if (!hasAnySelected) {
        errors[`${tabPath}.postback_events_and_platforms`] = err('POSTBACK_EVENTS_AND_PLATFORMS_REQUIRED');
    }
    if (!data.postback_url_base?.trim()) {
        errors[`${tabPath}.postback_url_base`] = err('POSTBACK_URL_BASE_REQUIRED');
    }
    if (data.different_domains_per_postback) {
        const hasAnyEventUrl = POSTBACK_EVENT_TYPE_OPTIONS.some(et => !!data[`${et}_postback_url`]?.trim());
        if (!hasAnyEventUrl) {
            errors[`${tabPath}.postback_urls`] = err('POSTBACK_URLS_REQUIRED_WHEN_DIFFERENT_DOMAINS');
        }
    }
    if (data.needs_app_level_macro) {
        const appMacros = data.app_level_macros || [];
        if (!appMacros.length) {
            errors[`${tabPath}.app_level_macros`] = err('APP_LEVEL_MACROS_REQUIRED');
        }
        appMacros.forEach((m, i) => {
            if (!m.parameter_key_name?.trim()) {
                errors[`${tabPath}.app_level_macros.${i}.parameter_key_name`] = err(
                    'APP_LEVEL_MACRO_PARAMETER_KEY_REQUIRED'
                );
            }
            if (!m.input_type) {
                errors[`${tabPath}.app_level_macros.${i}.input_type`] = err('APP_LEVEL_MACRO_INPUT_TYPE_REQUIRED');
            }
            if (m.input_type === MACRO_INPUT_TYPES.DROPDOWN && !m.allowed_values?.trim()) {
                errors[`${tabPath}.app_level_macros.${i}.allowed_values`] = err(
                    'APP_LEVEL_MACRO_ALLOWED_VALUES_REQUIRED'
                );
            }
        });
    }
    if (data.enable_event_mapping) {
        if (!data.event_parameter_key_name?.trim()) {
            errors[`${tabPath}.event_parameter_key_name`] = err('EVENT_PARAMETER_KEY_REQUIRED');
        } else if (WHITESPACE_REGEX.test(data.event_parameter_key_name)) {
            errors[`${tabPath}.event_parameter_key_no_spaces`] = err('EVENT_PARAMETER_KEY_NO_SPACES_SUMMARY');
        }
        const eventNames = data.event_names || [];
        if (!eventNames.length) {
            errors[`${tabPath}.event_names`] = err('EVENT_NAMES_REQUIRED');
        } else if (eventNames.some(n => !n?.trim())) {
            // ``addEventName`` seeds rows with '', so a forgotten empty row would otherwise pass
            // once at least one other row was filled in.
            errors[`${tabPath}.event_names_empty`] = err('EVENT_NAME_REQUIRED');
        }
        if (eventNames.some(n => n && WHITESPACE_REGEX.test(n))) {
            errors[`${tabPath}.event_names_no_spaces`] = err('EVENT_NAME_NO_SPACES_SUMMARY');
        }
        // Empty strings are duplicate-checked too: two empty rows are still two rows the user must fix.
        // Scoped to enable_event_mapping=true so server-seeded event_names with the section disabled
        // can't silently block Save Draft via the matching DRAFT_BLOCKING_ERROR_KEY_SUFFIX.
        const seenNames = new Set();
        const hasDuplicateEventName = eventNames.some(n => {
            const key = (n || '').trim();
            if (seenNames.has(key)) return true;
            seenNames.add(key);
            return false;
        });
        if (hasDuplicateEventName) {
            errors[`${tabPath}.event_names_duplicate`] = err('EVENT_NAME_DUPLICATE');
        }
    }
    return errors;
};

const validatePostbackTemplates = (data, formData) => {
    const errors = {};
    const tabPath = `formData.${ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES}`;
    const overallTab = formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP] || {};
    const entries = overallTab.postback_events_and_platforms || [];
    entries.forEach(entry => {
        const eventType = entry.event_type;
        (entry.platforms || []).forEach(platform => {
            const rows = data?.[eventType]?.[platform] || [];
            const filledRows = rows.filter(r => !!r.partner_parameter_key?.trim());
            // Per-table uniqueness: two rows can't share the same partner_parameter_key — they
            // would collide as duplicate query keys in the rendered postback URL.
            const keyCounts = filledRows.reduce((acc, r) => {
                const k = r.partner_parameter_key.trim();
                acc[k] = (acc[k] || 0) + 1;
                return acc;
            }, {});
            const duplicateKey = Object.keys(keyCounts).find(k => keyCounts[k] > 1);
            if (duplicateKey) {
                errors[`${tabPath}.${eventType}.${platform}.partner_parameter_key_duplicate`] = {
                    messageKey: err('POSTBACK_TEMPLATE_PARAM_KEY_DUPLICATE'),
                    eventType,
                    platform,
                    macro: duplicateKey,
                };
            }
            rows.forEach((row, i) => {
                if (!row.partner_parameter_key?.trim()) {
                    errors[`${tabPath}.${eventType}.${platform}.${i}.partner_parameter_key_required`] = {
                        messageKey: err('POSTBACK_TEMPLATE_PARAM_KEY_REQUIRED_ROW'),
                        eventType,
                        platform,
                        macro: row.singular_macro,
                    };
                }
                if (row.partner_parameter_key && !URL_QUERY_KEY_REGEX.test(row.partner_parameter_key)) {
                    errors[`${tabPath}.${eventType}.${platform}.${i}.partner_parameter_key_invalid_chars`] = {
                        messageKey: err('POSTBACK_TEMPLATE_PARAM_KEY_INVALID_CHARS_SUMMARY'),
                        eventType,
                        platform,
                        macro: row.singular_macro,
                    };
                }
            });
        });
    });
    return errors;
};

export const TAB_VALIDATORS = {
    [ATTRIBUTION_TAB_IDS.GENERAL]: data => validateRequired(ATTRIBUTION_TAB_IDS.GENERAL, data, GENERAL_RULES),
    [ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS]: validateTrackingLinkOptions,
    [ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP]: validatePostbackOverallSetup,
    [ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES]: validatePostbackTemplates,
};

const POSTBACK_TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const PLATFORM_LABEL_PREFIX = `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS`;
const TRACKING_LINK_OPTIONS_TAB_PREFIX = `${TRANSLATE_PREFIX}.TRACKING_LINK_OPTIONS_TAB`;

// Turn a validator-produced info object into the `data` object passed to <Translate>.
// Validators run as pure functions without access to `translate`, so any label
// composition that needs translation happens here at render time.
export const buildErrorData = (info, translate) => {
    if (!info || typeof info !== 'object') return undefined;
    const data = {};
    const translateTrackingParamFieldName = k => {
        const key = `${TRACKING_LINK_OPTIONS_TAB_PREFIX}.PARAMS.${String(k).toUpperCase()}.FIELD_NAME`;
        const label = translate(key);
        return label === key ? String(k) : label;
    };
    if (info.eventType && info.platform) {
        const eventLabel = translate(`${POSTBACK_TAB_PREFIX}.EVENT_TYPES.${info.eventType.toUpperCase()}`);
        const platformLabel = translate(`${PLATFORM_LABEL_PREFIX}.${info.platform.toUpperCase()}`);
        data.combo = `${eventLabel} - ${platformLabel}`;
    }
    if (info.macro) data.macro = info.macro;
    if (Array.isArray(info.requiredKeys) && info.requiredKeys.length) {
        data.requiredKeysList = info.requiredKeys.map(translateTrackingParamFieldName).join(', ');
    }
    if (
        Array.isArray(info.triggeringPlatforms) &&
        info.triggeringPlatforms.length &&
        Array.isArray(info.missingKeys) &&
        info.missingKeys.length
    ) {
        const platformsList = info.triggeringPlatforms
            .map(p => translate(`${PLATFORM_LABEL_PREFIX}.${String(p).toUpperCase()}`))
            .join(', ');
        const keysList = info.missingKeys.map(translateTrackingParamFieldName).join(', ');
        data.platformsList = platformsList;
        data.keysList = keysList;
    }
    if (info.fieldName) data.fieldName = info.fieldName;
    return Object.keys(data).length ? data : undefined;
};
